import React, { useState, useEffect } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getPrimaryImageUrl, getFallbackImageUrl } from '../../utils/imageUtils';
import { AdPlacement, AD_PLACEMENTS } from '../../components/ads';
import '../../themes/edition/EditionCategory.css';
import '../../themes/atelier/AtelierCategory.css';
import '../../themes/gallery/GalleryCategory.css';

// Helper to check if we're on a subdomain
const isOnSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.includes('.') && !hostname.startsWith('www.');
};

const CategoryPage = () => {
  const { category } = useParams();
  const { themeStyles, store, premiumThemeConfig } = useOutletContext() || { themeStyles: {}, store: {} };
  const isEditionTheme = premiumThemeConfig?.theme?.slug === 'edition';
  const isAtelierTheme = premiumThemeConfig?.theme?.slug === 'atelier';
  const isGalleryTheme = premiumThemeConfig?.theme?.slug === 'gallery';

  // State for API-fetched categories with hierarchy
  const [apiCategories, setApiCategories] = useState([]);

  // Get currency symbol
  const currencySymbol = store?.settings?.currency || '₦';

  // Merge premium theme global styles when available
  const globalOverrides = premiumThemeConfig?.globalOverrides || {};
  const premiumColors = premiumThemeConfig?.theme?.globalSettings?.colors || {};
  const premiumFonts = premiumThemeConfig?.theme?.globalSettings?.fonts || {};

  // Color helpers - prefer premium theme colors over basic themeStyles
  const primaryColor = globalOverrides?.colors?.primary || premiumColors?.primary || themeStyles?.primary || '#2563eb';
  const backgroundColor = globalOverrides?.colors?.background || premiumColors?.background || themeStyles?.background || '#ffffff';
  const backgroundAlt = globalOverrides?.colors?.backgroundAlt || premiumColors?.backgroundAlt || themeStyles?.backgroundAlt || '#f9fafb';
  const borderColor = globalOverrides?.colors?.border || premiumColors?.border || themeStyles?.border || '#e5e7eb';
  const textColor = globalOverrides?.colors?.text || premiumColors?.text || themeStyles?.text || '#111827';
  const mutedColor = globalOverrides?.colors?.muted || premiumColors?.muted || themeStyles?.textLight || '#6b7280';
  const accentColor = globalOverrides?.colors?.accent || premiumColors?.accent || themeStyles?.accent || '#10b981';
  const borderRadius = themeStyles?.borderRadius || '12px';
  const boxShadow = themeStyles?.shadow || '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
  const headingFont = premiumFonts?.heading || 'inherit';
  const bodyFont = premiumFonts?.body || 'inherit';

  // Check if we're on a subdomain
  const onSubdomain = isOnSubdomain();

  // Helper to generate the correct path based on subdomain vs path-based routing
  const getStorePath = (path = '') => {
    if (onSubdomain) {
      // On subdomain: use root-level paths
      return path ? `/${path}` : '/';
    } else {
      // Path-based: use /s/:slug/ format
      return path ? `/s/${store?.slug}/${path}` : `/s/${store?.slug}`;
    }
  };
  const products = useSelector(state => state.store?.products || []);

  // State for filters
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');

  // Fetch categories from API with hierarchy
  useEffect(() => {
    const fetchCategories = async () => {
      if (!store?.slug) return;
      try {
        const response = await fetch(`/api/categories/store/${store.slug}`);
        const data = await response.json();
        if (data.success && data.data) {
          setApiCategories(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, [store?.slug]);

  // Get all categories for sidebar (fallback to product strings if no API categories)
  const allCategories = React.useMemo(() => {
    return [...new Set(products.filter(p => p.status === 'ACTIVE').map(p => p.category).filter(Boolean))];
  }, [products]);

  // Get unique types and brands from products
  const productTypes = React.useMemo(() => {
    return [...new Set(products.filter(p => p.status === 'ACTIVE' && p.type).map(p => p.type).filter(Boolean))];
  }, [products]);

  const productBrands = React.useMemo(() => {
    return [...new Set(products.filter(p => p.status === 'ACTIVE' && p.brand).map(p => p.brand).filter(Boolean))];
  }, [products]);

  // Get subcategory slugs/names for current category (if it's a parent)
  const currentCategoryData = React.useMemo(() => {
    const parentCat = apiCategories.find(c =>
      c.slug === category || c.name.toLowerCase() === category?.toLowerCase()
    );
    if (parentCat && parentCat.children?.length > 0) {
      return {
        isParent: true,
        childSlugs: parentCat.children.map(c => c.slug.toLowerCase()),
        childNames: parentCat.children.map(c => c.name.toLowerCase())
      };
    }
    return { isParent: false, childSlugs: [], childNames: [] };
  }, [apiCategories, category]);

  // Filter products by category and active status, then apply additional filters
  // If viewing a parent category, also include products from its subcategories
  let categoryProducts = products?.filter(product => {
    if (product.status !== 'ACTIVE') return false;
    const prodCat = product.category?.toLowerCase();

    // Direct match
    if (prodCat === category?.toLowerCase()) return true;

    // If current category is a parent, also match subcategory products
    if (currentCategoryData.isParent) {
      if (currentCategoryData.childSlugs.includes(prodCat)) return true;
      if (currentCategoryData.childNames.includes(prodCat)) return true;
    }

    return false;
  }) || [];

  // Apply price filter
  if (selectedPriceRange !== 'all') {
    categoryProducts = categoryProducts.filter(product => {
      const price = parseFloat(product.price);
      switch (selectedPriceRange) {
        case 'under-1000':
          return price < 1000;
        case '1000-5000':
          return price >= 1000 && price < 5000;
        case '5000-10000':
          return price >= 5000 && price < 10000;
        case 'over-10000':
          return price >= 10000;
        default:
          return true;
      }
    });
  }

  // Apply type filter
  if (selectedType !== 'all') {
    categoryProducts = categoryProducts.filter(product => product.type === selectedType);
  }

  // Apply brand filter
  if (selectedBrand !== 'all') {
    categoryProducts = categoryProducts.filter(product => product.brand === selectedBrand);
  }

  return (
    <>
      <style>{`
        /* Mobile Optimizations for CategoryPage */
        @media (max-width: 768px) {
          .category-header-container {
            padding: 0 1rem !important;
          }
          .category-page-title {
            font-size: 2rem !important;
          }
          .category-page-subtitle {
            font-size: 1rem !important;
          }
          .category-main-container {
            padding: 2rem 1rem !important;
          }
          .category-layout {
            grid-template-columns: 1fr !important;
            gap: 1.5rem !important;
          }
          .category-sidebar {
            position: static !important;
            width: 100% !important;
            padding: 1rem !important;
          }
          .category-products-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 1rem !important;
          }
          .category-product-card {
            padding: 1rem !important;
          }
          .category-product-title {
            font-size: 1rem !important;
          }
          .category-product-price {
            font-size: 1.25rem !important;
          }
        }

        @media (max-width: 480px) {
          .category-header-container {
            padding: 0 0.75rem !important;
          }
          .category-page-title {
            font-size: 1.5rem !important;
          }
          .category-page-subtitle {
            font-size: 0.9rem !important;
          }
          .category-main-container {
            padding: 1.5rem 0.75rem !important;
          }
          .category-sidebar {
            padding: 0.75rem !important;
          }
          .category-products-grid {
            grid-template-columns: 1fr !important;
            gap: 0.75rem !important;
          }
        }
      `}</style>
      <div className={isEditionTheme ? 'edition-category' : isAtelierTheme ? 'atelier-category' : isGalleryTheme ? 'gallery-category' : ''} style={{ minHeight: '100vh', backgroundColor, fontFamily: bodyFont }}>
        {/* Header */}
        <div className="category-topbar" style={{
          backgroundColor,
          borderBottom: `1px solid ${borderColor}`,
          padding: '16px 0'
        }}>
          <div className="category-header-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Link
              to={getStorePath()}
              style={{
                display: 'flex',
                alignItems: 'center',
                color: primaryColor,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                textDecoration: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                border: `1px solid ${borderColor}`
              }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Store
            </Link>
            <div style={{
              fontSize: '14px',
              color: mutedColor,
              textTransform: 'capitalize'
            }}>
              {category} Category
            </div>
          </div>
        </div>
      </div>

      <div className="category-main-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '48px 24px' }}>
        {/* Category Header */}
        <div className="category-page-header" style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 className="category-page-title" style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: textColor,
            textTransform: 'capitalize'
          }}>
            {category} Products
          </h1>
          <p className="category-page-subtitle" style={{
            fontSize: '1.125rem',
            color: mutedColor,
            marginBottom: '1rem'
          }}>
            {categoryProducts.length} product{categoryProducts.length !== 1 ? 's' : ''} found
          </p>
        </div>

        {/* Main Content - Sidebar and Products */}
        <div className="category-layout" style={{
          display: 'grid',
          gridTemplateColumns: '280px 1fr',
          gap: '2rem'
        }}>
          {/* Sidebar with Filters */}
          <div className="category-sidebar" style={{
            backgroundColor: themeStyles?.background || 'white',
            borderRadius: '12px',
            padding: '1.5rem',
            height: 'fit-content',
            position: 'sticky',
            top: '100px',
            border: `1px solid ${borderColor}`
          }}>
            {/* Categories Section */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                marginBottom: '0.75rem',
                color: mutedColor,
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Categories
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {apiCategories.length > 0 ? (
                  apiCategories.map(parentCat => {
                    const isParentActive = parentCat.slug === category || parentCat.name.toLowerCase() === category?.toLowerCase();
                    const totalProducts = (parentCat._count?.products || 0) +
                      (parentCat.children?.reduce((sum, c) => sum + (c._count?.products || 0), 0) || 0);

                    return (
                      <React.Fragment key={parentCat.id}>
                        <Link
                          to={getStorePath(`category/${parentCat.slug}`)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            borderRadius: '6px',
                            textDecoration: 'none',
                            color: isParentActive ? primaryColor : textColor,
                            backgroundColor: isParentActive ? `${primaryColor}10` : 'transparent',
                            fontWeight: '500',
                            fontSize: '0.875rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={(e) => {
                            if (!isParentActive) e.currentTarget.style.backgroundColor = backgroundAlt;
                          }}
                          onMouseLeave={(e) => {
                            if (!isParentActive) e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <span>{parentCat.name}</span>
                          <span style={{ fontSize: '0.75rem', color: mutedColor }}>{totalProducts}</span>
                        </Link>
                        {parentCat.children?.map(childCat => {
                          const isChildActive = childCat.slug === category || childCat.name.toLowerCase() === category?.toLowerCase();
                          return (
                            <Link
                              key={childCat.id}
                              to={getStorePath(`category/${childCat.slug}`)}
                              style={{
                                padding: '0.4rem 0.75rem 0.4rem 1.5rem',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                color: isChildActive ? primaryColor : mutedColor,
                                backgroundColor: isChildActive ? `${primaryColor}10` : 'transparent',
                                fontWeight: isChildActive ? '500' : '400',
                                fontSize: '0.813rem',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={(e) => {
                                if (!isChildActive) {
                                  e.currentTarget.style.backgroundColor = backgroundAlt;
                                  e.currentTarget.style.color = textColor;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isChildActive) {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = mutedColor;
                                }
                              }}
                            >
                              <span>{childCat.name}</span>
                              <span style={{ fontSize: '0.7rem' }}>{childCat._count?.products || 0}</span>
                            </Link>
                          );
                        })}
                      </React.Fragment>
                    );
                  })
                ) : (
                  allCategories.map(cat => {
                    const isActive = cat.toLowerCase() === category?.toLowerCase();
                    return (
                      <Link
                        key={cat}
                        to={getStorePath(`category/${cat.toLowerCase()}`)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          color: isActive ? primaryColor : textColor,
                          backgroundColor: isActive ? `${primaryColor}10` : 'transparent',
                          fontWeight: '500',
                          fontSize: '0.875rem',
                          textTransform: 'capitalize',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'background 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) e.currentTarget.style.backgroundColor = backgroundAlt;
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span>{cat}</span>
                        <span style={{ fontSize: '0.75rem', color: mutedColor }}>
                          {products.filter(p => p.category === cat && p.status === 'ACTIVE').length}
                        </span>
                      </Link>
                    );
                  })
                )}
              </div>
            </div>

            {/* Price Filter */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                fontSize: '1rem',
                fontWeight: '700',
                marginBottom: '1rem',
                color: textColor,
                paddingBottom: '0.75rem',
                borderBottom: `2px solid ${primaryColor}`
              }}>
                Price Range
              </h3>
              <select
                value={selectedPriceRange}
                onChange={(e) => setSelectedPriceRange(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  border: `1px solid ${borderColor}`,
                  borderRadius: '8px',
                  outline: 'none',
                  backgroundColor: themeStyles?.background || 'white',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  color: textColor
                }}
              >
                <option value="all">All Prices</option>
                <option value="under-1000">Under {currencySymbol}1,000</option>
                <option value="1000-5000">{currencySymbol}1,000 - {currencySymbol}5,000</option>
                <option value="5000-10000">{currencySymbol}5,000 - {currencySymbol}10,000</option>
                <option value="over-10000">Over {currencySymbol}10,000</option>
              </select>
            </div>

            {/* Type Filter */}
            {productTypes.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  color: textColor,
                  paddingBottom: '0.75rem',
                  borderBottom: `2px solid ${primaryColor}`
                }}>
                  Type
                </h3>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: themeStyles?.background || 'white',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: textColor
                  }}
                >
                  <option value="all">All Types</option>
                  {productTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Brand Filter */}
            {productBrands.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{
                  fontSize: '1rem',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  color: textColor,
                  paddingBottom: '0.75rem',
                  borderBottom: `2px solid ${primaryColor}`
                }}>
                  Brand
                </h3>
                <select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: themeStyles?.background || 'white',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: textColor
                  }}
                >
                  <option value="all">All Brands</option>
                  {productBrands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Clear Filters Button */}
            {(selectedPriceRange !== 'all' || selectedType !== 'all' || selectedBrand !== 'all') && (
              <button
                onClick={() => {
                  setSelectedPriceRange('all');
                  setSelectedType('all');
                  setSelectedBrand('all');
                }}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  color: primaryColor,
                  backgroundColor: 'transparent',
                  border: `2px solid ${primaryColor}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginTop: '1rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = primaryColor;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = primaryColor;
                }}
              >
                Clear All Filters
              </button>
            )}
          </div>

          {/* Products Grid */}
          <div>
            {categoryProducts.length > 0 ? (
          <div className="category-products-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {categoryProducts.map((product, index) => (
              <React.Fragment key={product._id}>
                {/* Insert inline ad after every 6 products */}
                {index > 0 && index % 6 === 0 && store?.slug && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <AdPlacement
                      storeSlug={store.slug}
                      placement={AD_PLACEMENTS.CATEGORY_INLINE}
                      showAdvertiseBlurb={index === 6}
                    />
                  </div>
                )}
              <Link
                className="category-product-link"
                to={getStorePath(`product/${product.slug}`)}
                style={{
                  textDecoration: 'none',
                  color: 'inherit'
                }}
              >
                <div
                  className="category-product-surface"
                  style={{
                    backgroundColor: themeStyles?.background || 'white',
                    borderRadius: borderRadius,
                    boxShadow: boxShadow,
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = boxShadow;
                  }}
                >
                  {/* Product Image */}
                  <div className="category-product-image" style={{
                    height: '200px',
                    backgroundColor: borderColor,
                    backgroundImage: getPrimaryImageUrl(product) ? `url(${getPrimaryImageUrl(product)})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {!getPrimaryImageUrl(product) && (
                      <div style={{
                        color: mutedColor,
                        fontSize: '3rem',
                        opacity: 0.5
                      }}>
                        📦
                      </div>
                    )}
                    {product.featured && (
                      <div style={{
                        position: 'absolute',
                        top: '0.5rem',
                        left: '0.5rem',
                        backgroundColor: accentColor,
                        color: 'white',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: '600'
                      }}>
                        Featured
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="category-product-card" style={{ padding: '1.5rem' }}>
                    <h3 className="category-product-title" style={{
                      fontSize: '1.25rem',
                      fontWeight: '600',
                      marginBottom: '0.5rem',
                      color: textColor,
                      lineHeight: '1.3'
                    }}>
                      {product.name}
                    </h3>

                    <p style={{
                      color: mutedColor,
                      marginBottom: '1rem',
                      fontSize: '0.875rem',
                      lineHeight: '1.5',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {product.shortDescription || product.description?.substring(0, 100) + '...'}
                    </p>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                      <div>
                        <span className="category-product-price" style={{
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          color: primaryColor
                        }}>
                          {currencySymbol}{parseFloat(product.price)?.toFixed(2) || '0.00'}
                        </span>
                        {product.compareAtPrice && parseFloat(product.compareAtPrice) > parseFloat(product.price) && (
                          <span style={{
                            fontSize: '1rem',
                            color: mutedColor,
                            textDecoration: 'line-through',
                            marginLeft: '0.5rem'
                          }}>
                            {currencySymbol}{parseFloat(product.compareAtPrice)?.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <span style={{
                        fontSize: '0.875rem',
                        color: mutedColor,
                        textTransform: 'capitalize'
                      }}>
                        {product.category}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'block',
                        width: '100%',
                        backgroundColor: primaryColor,
                        color: 'white',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        textAlign: 'center',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                        minHeight: '44px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      View Details
                    </div>
                  </div>
                </div>
              </Link>
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '1rem',
              opacity: 0.5
            }}>
              📦
            </div>
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: textColor,
              marginBottom: '0.5rem'
            }}>
              No products found
            </h2>
            <p style={{
              color: mutedColor,
              marginBottom: '2rem'
            }}>
              No products available in the {category} category
            </p>
            <Link
              to={getStorePath()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                backgroundColor: primaryColor,
                color: 'white',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'all 0.2s ease'
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Store
            </Link>
          </div>
            )}
          </div>
          {/* End Products Grid Wrapper */}
        </div>
        {/* End Main Grid (Sidebar + Products) */}
      </div>
      {/* End Main Container */}
    </div>
    </>
  );
};

export default CategoryPage;
